import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import authService from "@/services/authService";
import { extractErrorMessage } from "@/utils/errors";

interface ResetPasswordFormValues {
  new_password: string;
  confirm_password: string;
}

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>();

  const newPassword = watch("new_password");

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      await authService.resetPassword(token, values.new_password);
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (error) {
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Alert severity="error">
        This password reset link is missing a token. Please request a new one.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        {success && <Alert severity="success">Password reset! Redirecting to login...</Alert>}

        <TextField
          label="New password"
          type="password"
          fullWidth
          error={Boolean(errors.new_password)}
          helperText={
            errors.new_password?.message ??
            "Min 8 characters, with uppercase, lowercase, digit & special character"
          }
          {...register("new_password", {
            required: "New password is required",
            pattern: { value: PASSWORD_PATTERN, message: "Password does not meet complexity rules" },
          })}
        />

        <TextField
          label="Confirm new password"
          type="password"
          fullWidth
          error={Boolean(errors.confirm_password)}
          helperText={errors.confirm_password?.message}
          {...register("confirm_password", {
            required: "Please confirm your password",
            validate: (value) => value === newPassword || "Passwords do not match",
          })}
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? "Resetting..." : "Reset Password"}
        </Button>

        <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
          Back to sign in
        </Link>
      </Stack>
    </Box>
  );
}

export default ResetPasswordPage;
